import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useT } from '../../i18n';

// ─────────────────────────────────────────────────────────────────────
// ASKING BEFORE SOMETHING DESTRUCTIVE, IN THE PAGE (#589).
//
// `DOMAIN_RULES` §9: never `window.confirm()`. It is blocked in an iframe and
// simply DOES NOTHING — and «nothing» is a return value, so the two shapes fail
// in opposite directions and both are silent:
//
//   if (!confirm('Slett?')) return;    → returns false → the delete never runs,
//                                        and the button looks broken
//   if (confirm('Avbryt?')) { … }      → returns false → the action never runs,
//                                        and the tournament cannot be abandoned
//
// Either way the user presses a control and nothing happens, with nothing said.
// The changelog records this being replaced once already, in the monolith; it
// came back, which is why #589 asks for a guard rather than another sweep.
//
// ── WHY A BUTTON RATHER THAN A DIALOG ────────────────────────────────
//
// It replaces itself with the question, in place. That is the idiom the repo
// already reached for twice on its own — `SetupDropdown` and
// `BookingZoneEditor` both hold an id in state and swap the row for a
// confirm/cancel pair — so this is those two generalised rather than a new
// modal system nobody asked for. It also keeps the question where the user is
// already looking, which is the same principle as #369.
// ─────────────────────────────────────────────────────────────────────

export interface ConfirmButtonProps {
  /** What the button does once confirmed. */
  onConfirm: () => void;
  /** The question. Defaults to a generic one; pass the specific stakes. */
  question?: ReactNode;
  /** The affirmative label — say what will happen, not «OK». */
  confirmLabel?: ReactNode;
  /** The trigger's own label. */
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  /** Applied to the armed state's container, for surfaces with their own look. */
  armedClassName?: string;
  disabled?: boolean;
}

/**
 * A destructive button that asks first, inline.
 *
 * ```tsx
 * <ConfirmButton
 *   question="Slett pauset turnering? All data går tapt."
 *   confirmLabel="Slett"
 *   onConfirm={() => onDeletePaused(id)}
 *   className="btn-ghost text-xs text-rose"
 * >
 *   Slett
 * </ConfirmButton>
 * ```
 */
export function ConfirmButton({
  onConfirm,
  question,
  confirmLabel,
  children,
  className,
  style,
  title,
  armedClassName,
  disabled,
}: ConfirmButtonProps) {
  const t = useT();
  const [armed, setArmed] = useState(false);
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  // MOVE TO THE ANSWER, so the keyboard does not have to hunt for it — and so a
  // screen reader announces the question rather than leaving the user on a
  // trigger that has silently changed meaning.
  useEffect(() => {
    if (armed) confirmRef.current?.focus();
  }, [armed]);

  // ESCAPE DISARMS. A confirmation with no way out but «yes» is a trap, and the
  // native dialog this replaces always had one.
  //
  // ON `window` IN CAPTURE, AND CLAIMED (#900). Inside a `Dialog` the first
  // Escape belongs to this question, not to the dialog around it: the saved-KM-
  // setups list asks «Slette oppsett …?» inline, and an Escape meant as «no»
  // closed the whole list. `Dialog` listens on `document` in capture, so the
  // only place earlier than it is `window` in capture; `preventDefault()` is
  // the signal it honours. Outside a dialog nothing reads that flag.
  useEffect(() => {
    if (!armed) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setArmed(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [armed]);

  if (!armed) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setArmed(true)}
        className={className}
        style={style}
        title={title}
      >
        {children}
      </button>
    );
  }

  return (
    <span
      role="alertdialog"
      aria-label={typeof question === 'string' ? question : t('ui.confirm.question')}
      className={armedClassName ?? 'inline-flex items-center gap-2 flex-wrap'}
    >
      <span className="text-[11px] font-medium text-ink-2">
        {question ?? t('ui.confirm.question')}
      </span>
      <button
        ref={confirmRef}
        type="button"
        onClick={() => {
          // DISARM FIRST — the ORDER of the two calls, not their visible
          // effect. `onConfirm` often unmounts this (navigating home, removing
          // the row it sits in), and queueing the update while the component
          // still exists lets React discard it on unmount. The other order sets
          // state on something that is already gone.
          //
          // Nothing has re-rendered by the time `onConfirm` runs — React
          // batches — so this is not a way to observe a disarmed button.
          //
          // AND NO TEST COVERS THE ORDER, said plainly: React 19 removed the
          // set-state-after-unmount warning, so swapping these two lines
          // changes nothing observable and the suite stays green (checked by
          // swapping them). It is defence that costs nothing, not a behaviour
          // anyone can assert on.
          setArmed(false);
          onConfirm();
        }}
        className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded text-rose hover:bg-rose-soft"
      >
        {confirmLabel ?? t('ui.confirm.confirm')}
      </button>
      <button
        type="button"
        onClick={() => setArmed(false)}
        className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded text-ink-3 hover:text-ink"
      >
        {t('ui.confirm.cancel')}
      </button>
    </span>
  );
}
